import { Anchor, Badge, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuShip } from 'react-icons/lu'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { useBoats } from '@/frontend/hooks/useBoats'

const FT_PER_METER = 3.28084

export const Route = createFileRoute('/dashboard/accounts/boats/')({
  component: BoatsList,
})

function BoatsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useCanWrite()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useBoats({ q: query, page, pageSize })
  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuShip size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.accounts.boats.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.accounts.boats.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/accounts/boats/new' as any })}>
              {t('page.accounts.boats.btn_new')}
            </Button>
          )}
        </Group>

        <Group justify="flex-end">
          <input
            type="search"
            value={search}
            placeholder={t('page.accounts.boats.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(search)
                setPage(1)
              }
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 240,
              outline: 'none',
            }}
          />
        </Group>

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={t('page.accounts.boats.empty')}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            columns={
              <>
                <Table.Th>{t('page.accounts.boats.col_boat')}</Table.Th>
                <Table.Th>{t('page.accounts.boats.col_owner')}</Table.Th>
                <Table.Th>{t('page.accounts.boats.col_dimensions')}</Table.Th>
                <Table.Th>{t('common.country')}</Table.Th>
                <Table.Th>{t('page.accounts.boats.col_location')}</Table.Th>
                <Table.Th ta="right">{t('page.accounts.boats.col_actions')}</Table.Th>
              </>
            }
          >
            {items.map((b) => {
              const customerName = b.customer ? `${b.customer.firstName} ${b.customer.lastName}`.trim() : '—'
              const inMarina = b.location && b.location !== 'Unknown' && b.location !== 'At Sea'
              return (
                <Table.Tr
                  key={b.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate({ to: `/dashboard/accounts/boats/${b.id}` as any })}
                >
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {b.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatType(b.type)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{customerName}</Text>
                    {b.customer?.category && <KindBadge category={b.customer.category} />}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{fmtFt(b.loaMeters)} LOA</Text>
                    <Text size="xs" c="dimmed">
                      {fmtFt(b.beamMeters)} beam{b.draftMeters ? ` · ${fmtFt(b.draftMeters)} draft` : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{b.flagState || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={inMarina ? 'laut' : b.location === 'At Sea' ? 'blue' : 'gray'}
                      size="sm"
                    >
                      {!b.location
                        ? t('page.accounts.boats.unknown')
                        : b.location === 'At Sea'
                          ? t('page.accounts.boats.at_sea')
                          : b.location === 'Unknown'
                            ? t('page.accounts.boats.unknown')
                            : b.location}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Anchor
                      component={Link}
                      to={`/dashboard/accounts/boats/${b.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {t('common.see_more')}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}

function formatType(t: string): string {
  return t
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ')
}

function fmtFt(meters: string | null | undefined): string {
  if (!meters) return '-'
  const m = Number(meters)
  if (!Number.isFinite(m) || m === 0) return '-'
  return `${(m * FT_PER_METER).toFixed(1)} ft`
}
